/**
 * ElevationProfile Component
 * 
 * Отображает профиль высот маршрута с плавной изгибающейся линией.
 * 
 * Props:
 * - geometryPoints: Array - массив точек с полями { altitude, distance, latitude, longitude }
 * - height: Number - высота графика в пикселях (по умолчанию 200)
 * 
 * Особенности:
 * - Использует Catmull-Rom сплайны для создания плавной кривой
 * - Интерактивный tooltip при наведении мыши
 * - Адаптивная ширина (100%)
 * - Статистика: min/max высота, набор высоты, общая дистанция
 * 
 * Пример использования:
 * <ElevationProfile 
 *   geometryPoints={route.parameters.simplifiedGeometryPoints}
 *   height={160}
 * />
 */

import React, { useId, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './ElevationProfile.css';

// gain — набор высоты в метрах, если он известен точнее, чем по упрощенной
// геометрии (ROUVY отдает ascendedMeters). Без него показываем перепад max − min
const ElevationProfile = ({ geometryPoints, width = '100%', height = 200, showHeader = true, gain = null }) => {
  const { t } = useLanguage();
  const [hoveredPoint, setHoveredPoint] = useState(null);
  // В модалке профилей графиков несколько на странице: id градиента должен быть
  // у каждого свой, иначе все они ссылаются на один и тот же градиент
  const gradientId = `elevationGradient-${useId().replace(/:/g, '')}`;
  const containerWidth = 600; // Базовая ширина для расчетов
  const profileData = useMemo(() => {
    if (!geometryPoints || geometryPoints.length === 0) {
      return null;
    }

    // Находим min/max значения для масштабирования
    const distances = geometryPoints.map(p => p.distance);
    const altitudes = geometryPoints.map(p => p.altitude);
    
    const minDistance = Math.min(...distances);
    const maxDistance = Math.max(...distances);
    const minAltitude = Math.min(...altitudes);
    const maxAltitude = Math.max(...altitudes);
    
    // Добавляем padding для графика
    const padding = { top: 10, right: 30, bottom: 40, left: 50 };
    const chartWidth = containerWidth - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    // Масштабируем точки
    const points = geometryPoints.map(point => ({
      x: padding.left + ((point.distance - minDistance) / (maxDistance - minDistance)) * chartWidth,
      y: padding.top + chartHeight - ((point.altitude - minAltitude) / (maxAltitude - minAltitude)) * chartHeight,
      altitude: point.altitude,
      distance: point.distance
    }));
    
    // Создаем плавную кривую через все точки используя Catmull-Rom spline
    const createSmoothPath = (points) => {
      if (points.length < 2) return '';
      
      let path = `M ${points[0].x} ${points[0].y}`;
      
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(i - 1, 0)];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[Math.min(i + 2, points.length - 1)];
        
        // Вычисляем контрольные точки для кубической кривой Безье
        const tension = 0.5;
        const cp1x = p1.x + (p2.x - p0.x) / 6 * tension;
        const cp1y = p1.y + (p2.y - p0.y) / 6 * tension;
        const cp2x = p2.x - (p3.x - p1.x) / 6 * tension;
        const cp2y = p2.y - (p3.y - p1.y) / 6 * tension;
        
        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
      }
      
      return path;
    };
    
    const smoothPath = createSmoothPath(points);
    
    // Создаем путь для заливки
    const fillPath = smoothPath + 
      ` L ${points[points.length - 1].x} ${height - padding.bottom}` +
      ` L ${points[0].x} ${height - padding.bottom} Z`;
    
    // Форматирование чисел
    const formatDistance = (dist) => {
      return (dist / 1000).toFixed(1) + ' km';
    };
    
    const formatAltitude = (alt) => {
      return Math.round(alt) + ' m';
    };
    
    return {
      smoothPath,
      fillPath,
      points,
      minAltitude,
      maxAltitude,
      minDistance,
      maxDistance,
      padding,
      chartWidth,
      chartHeight,
      formatDistance,
      formatAltitude
    };
  }, [geometryPoints, height]);

  if (!profileData) {
    return null;
  }

  const {
    smoothPath,
    fillPath,
    points,
    minAltitude,
    maxAltitude,
    minDistance,
    maxDistance,
    padding,
    chartWidth,
    chartHeight,
    formatDistance,
    formatAltitude
  } = profileData;

  // Генерируем метки для оси X (расстояние) - только начало и конец
  const xAxisLabels = [
    { x: padding.left, distance: minDistance },
    { x: padding.left + chartWidth / 2, distance: (minDistance + maxDistance) / 2 },
    { x: padding.left + chartWidth, distance: maxDistance }
  ];

  // Генерируем метки для оси Y (высота) - только мин и макс
  const yAxisLabels = [
    { y: padding.top + chartHeight, altitude: minAltitude },
    { y: padding.top, altitude: maxAltitude }
  ];

  return (
    <div className="elevation-profile">
      {showHeader && (
        <div className="elevation-profile-header">
          <h4 className="elevation-profile-title">{t('elevationProfile.title')}</h4>
        </div>
      )}
      <svg width="100%" height={height} viewBox={`0 0 ${containerWidth} ${height}`} className="elevation-svg" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        
        {/* Рамка графика */}
        <rect
          x={padding.left}
          y={padding.top}
          width={chartWidth}
          height={chartHeight}
          fill="none"
          style={{ stroke: 'var(--c-e5e7eb-bd)' }}
          strokeWidth="1"
        />
        
        {/* Заливка под графиком */}
        <path
          d={fillPath}
          fill={`url(#${gradientId})`}
        />
        
        {/* Основная линия профиля */}
        <path
          d={smoothPath}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Метки оси Y (высота) */}
        {yAxisLabels.map((label, i) => (
          <text
            key={`label-y-${i}`}
            x={padding.left - 10}
            y={label.y}
            textAnchor="end"
            alignmentBaseline="middle"
            className="elevation-axis-label"
            fontSize="12"
            style={{ fill: 'var(--c-6b7280-fg)' }}
          >
            {Math.round(label.altitude)}m
          </text>
        ))}
        
        {/* Метки оси X (расстояние) */}
        {xAxisLabels.map((label, i) => (
          <text
            key={`label-x-${i}`}
            x={label.x}
            y={height - 10}
            textAnchor="middle"
            className="elevation-axis-label"
            fontSize="11"
            style={{ fill: 'var(--c-6b7280-fg)' }}
          >
            {formatDistance(label.distance)}
          </text>
        ))}
        
        {/* Невидимый overlay для отслеживания мыши */}
        <rect
          x={padding.left}
          y={padding.top}
          width={chartWidth}
          height={chartHeight}
          fill="transparent"
          onMouseMove={(e) => {
            const svg = e.currentTarget.ownerSVGElement;
            const svgRect = svg.getBoundingClientRect();
            const scaleX = containerWidth / svgRect.width;
            const mouseX = (e.clientX - svgRect.left) * scaleX;
            
            // Находим ближайшую точку
            let closestPoint = null;
            let minDistance = Infinity;
            
            points.forEach((point, index) => {
              const distance = Math.abs(point.x - mouseX);
              if (distance < minDistance) {
                minDistance = distance;
                closestPoint = { ...point, index };
              }
            });
            
            if (closestPoint && minDistance < 30) {
              setHoveredPoint(closestPoint);
            } else {
              setHoveredPoint(null);
            }
          }}
          onMouseLeave={() => setHoveredPoint(null)}
        />
        
        {/* Вертикальная линия при наведении */}
        {hoveredPoint && (
          <>
            <line
              x1={hoveredPoint.x}
              y1={padding.top}
              x2={hoveredPoint.x}
              y2={padding.top + chartHeight}
              stroke="#3b82f6"
              strokeWidth="1"
              strokeDasharray="4 4"
              opacity="0.5"
            />
            <circle
              cx={hoveredPoint.x}
              cy={hoveredPoint.y}
              r="5"
              fill="#3b82f6"
              stroke="white"
              strokeWidth="2"
            />
          </>
        )}
      </svg>
      
      {/* Tooltip при наведении */}
      {hoveredPoint && (
        <div className="elevation-tooltip">
          <div className="elevation-tooltip-row">
            <span className="elevation-tooltip-label">{t('elevationProfile.distance')}:</span>
            <span className="elevation-tooltip-value">{formatDistance(hoveredPoint.distance)}</span>
          </div>
          <div className="elevation-tooltip-row">
            <span className="elevation-tooltip-label">{t('elevationProfile.altitude')}:</span>
            <span className="elevation-tooltip-value">{formatAltitude(hoveredPoint.altitude)}</span>
          </div>
        </div>
      )}
      
      <div className="elevation-stats">
        <div className="elevation-stat">
          <span className="elevation-stat-label">{t('elevationProfile.min')}</span>
          <span className="elevation-stat-value">{formatAltitude(minAltitude)}</span>
        </div>
        <div className="elevation-stat">
          <span className="elevation-stat-label">{t('elevationProfile.max')}</span>
          <span className="elevation-stat-value">{formatAltitude(maxAltitude)}</span>
        </div>
        <div className="elevation-stat">
          <span className="elevation-stat-label">{t('elevationProfile.gain')}</span>
          <span className="elevation-stat-value">{formatAltitude(Number.isFinite(gain) ? gain : maxAltitude - minAltitude)}</span>
        </div>
        <div className="elevation-stat">
          <span className="elevation-stat-label">{t('elevationProfile.distance')}</span>
          <span className="elevation-stat-value">{formatDistance(maxDistance)}</span>
        </div>
      </div>
    </div>
  );
};

export default ElevationProfile;